/**
 * The resolution queue, and the calibration pack.
 *
 * Two readers over three stored fields — a node's `resolvesBy`, its `outcome`,
 * and the `confidenceBefore` that outcome froze — and the first is the second's
 * future. `resolvesBy` is a promise: a person who wrote a date against a claim
 * said they expect to know by then, and the queue is the list of those
 * promises, due first, with the overdue ones at the top because a promise that
 * fell due unanswered is the most useful row it has. Recording the outcome
 * keeps the promise, and the node leaves the queue for the pack — but only
 * while that outcome is still an observation of the claim on the node. A
 * rewritten claim is an unmeasured proposition again, and the promise it
 * carries has not been kept by a result about wording it no longer makes.
 *
 * The pack is `calibration.ts`'s ledger made portable. That module scores a
 * model's stated confidence against what a person later observed, and it
 * withholds every score below `MIN_SCORED` because four results are an
 * anecdote — which leaves the most distinctive honesty mechanism in the product
 * dormant for almost every exploration, since five results means five trips to
 * a lab. Results accrue across explorations, across machines and across the
 * colleagues who ran the experiments, and a ledger read from one browser's
 * library sees none of that. The pack is the rows the ledger is built from,
 * with what a recipient needs to rebuild it and nothing else: the claim text,
 * the frozen confidence, the bearing and its dates, and the stamp of the model
 * that gave the rating — the stamp because `calibrate` is per model and never
 * pooled, and a row with no model could only be scored by averaging two
 * scales. No reasoning, no evidence, no trace: nothing the app cannot re-derive
 * from its stored records, so a pack handed to someone who never saw the
 * exploration says nothing the graph does not already say. The summary it
 * carries is `calibrate`'s own tables, computed here and never restated, so a
 * pack and a ledger built from the same rows cannot disagree.
 *
 * The pack's exclusions are `loadOutcomeRecords`' own, for its reasons: the
 * demo's result was authored, not obtained; a node with no model stamp has no
 * rateable author; a node the person rewrote carries the person's number, not
 * the model's; and only `confidenceBefore` is ever read, because the live
 * `confidence` moves. And one row per RESULT, not per document carrying it —
 * the library holds deliberate copies, a fork carries its origin's outcomes
 * under the same node ids, and a result counted once per copy would score the
 * same observation twice.
 *
 * No model call in this file, and there must never be one, for the reason
 * `calibration.ts` gives: a model grading its own calibration is the
 * self-report this exists to replace. Runtime imports from `./calibration` and
 * `./persist` only, and `persist.ts` solely for the two library readers at the
 * bottom, which walk the same index every other library reader walks.
 */

import { calibrate, scoredShortfall, verdictOf, type ModelCalibration } from './calibration'
import { calendarDay } from './dates'
import { DEMO_SESSION_ID, listRecent, loadSession } from './persist'
import type { ModelStamp, Outcome, Session } from './types'
import { outcomeLive } from './types'

/** What the two readers need of an exploration: no ledger, no ops, no runs. */
export type ResolutionSource = Pick<Session, 'id' | 'seed' | 'nodes' | 'order'>

/* ── the queue ─────────────────────────────────────────────────────────── */

export interface ResolutionRow {
  sessionId: string
  /** The exploration's seed — what names it once the row is away from the sidebar. */
  seed: string
  nodeId: string
  label: string
  claim: string
  /** The date the person expects to know by, as stored: never clamped to now. */
  resolvesBy: number
  /** Milliseconds until it falls due; negative once it has. */
  dueInMs: number
  overdue: boolean
}

/**
 * Every promise not yet kept, due first.
 *
 * A node is in the queue exactly when it carries `resolvesBy` and no LIVE
 * `outcome` — `outcomeLive`, the same rule the calibration pack leans on eighty
 * lines down when it drops every edited node. `reviseNode` retains the outcome
 * and stamps `editedAt`, and nothing anywhere clears `resolvesBy`, so a bare
 * presence check left the replacement claim carrying a promise that could
 * neither be queued here nor edited on the card.
 * Nothing else is read — not `terminal`, not the kind, not the confidence —
 * because the field's own contract is that the queue is built from it and
 * nothing else, and a promise the person wrote against a dead branch is still
 * a promise. A node with an outcome has kept its promise and leaves, whatever
 * date it still carries: recording clears the date, but a document written
 * before it did should not queue a result already in hand.
 *
 * Due first, and within a date the older promise first, then the ids, so the
 * same explorations answer with the same list whatever order they arrive in.
 * `overdue` is strictly past: a promise due this millisecond is due, not late.
 */
export function resolutionQueue(
  sessions: Iterable<ResolutionSource>,
  now: number,
): ResolutionRow[] {
  const rows: Array<{ row: ResolutionRow; createdAt: number }> = []
  for (const s of sessions) {
    for (const id of s.order) {
      const n = s.nodes[id]
      if (!n || n.resolvesBy === undefined || (n.outcome && outcomeLive(n))) continue
      rows.push({
        createdAt: n.createdAt,
        row: {
          sessionId: s.id,
          seed: s.seed,
          nodeId: n.id,
          label: n.label,
          claim: n.claim,
          resolvesBy: n.resolvesBy,
          dueInMs: n.resolvesBy - now,
          overdue: n.resolvesBy < now,
        },
      })
    }
  }
  rows.sort(
    (p, q) =>
      p.row.resolvesBy - q.row.resolvesBy ||
      p.createdAt - q.createdAt ||
      cmp(p.row.sessionId, q.row.sessionId) ||
      cmp(p.row.nodeId, q.row.nodeId),
  )
  return rows.map((r) => r.row)
}

/* ── the pack ──────────────────────────────────────────────────────────── */

export interface CalibrationPackRow {
  sessionId: string
  seed: string
  nodeId: string
  /** The proposition the rating was given for. The one piece of model prose
   *  here, and the pack is unreadable without it. */
  claim: string
  /** The model that gave the rating — what `calibrate` groups by. */
  model: ModelStamp
  /** `Outcome.confidenceBefore`: the rating from before any result existed,
   *  written once and never touched by an edit. Never the live `confidence`. */
  confidence: number
  /** When the person expected to know, where they said. */
  resolvesBy?: number
  outcome: {
    bearing: Outcome['bearing']
    /** When the result was obtained — the observation's own date. */
    at: number
    /** When it was entered. */
    recordedAt: number
  }
}

export interface CalibrationPack {
  version: 1
  builtAt: number
  /** Oldest observation first, then by the ids, so two packs over one library
   *  are the same document. */
  rows: CalibrationPackRow[]
  /** `calibrate` over the rows: one table per model, busiest first. Re-derivable
   *  from `rows`, and carried so a reader with no app can read the numbers. */
  summary: ModelCalibration[]
}

/**
 * The pack, over whatever explorations it is handed.
 *
 * Pure: a caller with the library uses `libraryCalibrationPack`, and a test or
 * a merge hands it sessions directly. The demo is refused by id even here,
 * because a pre-authored result grading a real model is the artefact the rest
 * of this app exists to make impossible, and that has to hold whoever calls.
 */
export function calibrationPack(
  sessions: Iterable<ResolutionSource>,
  now: number,
): CalibrationPack {
  const rows: CalibrationPackRow[] = []
  // One row per result. The key is the one `persist.ts` uses for the same
  // purpose: a node id and a recording millisecond, which two different results
  // cannot share and two copies of one result cannot fail to.
  const seen = new Set<string>()
  for (const s of sessions) {
    if (s.id === DEMO_SESSION_ID) continue
    for (const id of s.order) {
      const n = s.nodes[id]
      if (!n?.outcome || !n.model) continue
      if (n.origin === 'manual' || n.editedAt !== undefined) continue
      const key = `${n.id} ${n.outcome.recordedAt}`
      if (seen.has(key)) continue
      seen.add(key)
      rows.push({
        sessionId: s.id,
        seed: s.seed,
        nodeId: n.id,
        claim: n.claim,
        model: n.model,
        confidence: n.outcome.confidenceBefore,
        // Spread rather than assigned, so an absent date is absent in the JSON
        // and a pack survives its own round trip byte for byte.
        ...(n.resolvesBy !== undefined ? { resolvesBy: n.resolvesBy } : {}),
        outcome: {
          bearing: n.outcome.bearing,
          at: n.outcome.at,
          recordedAt: n.outcome.recordedAt,
        },
      })
    }
  }
  rows.sort(
    (p, q) =>
      p.outcome.at - q.outcome.at ||
      p.outcome.recordedAt - q.outcome.recordedAt ||
      cmp(p.sessionId, q.sessionId) ||
      cmp(p.nodeId, q.nodeId),
  )
  return {
    version: 1,
    builtAt: now,
    rows,
    summary: calibrate(
      rows.map((r) => ({
        sessionId: r.sessionId,
        model: r.model,
        confidence: r.confidence,
        bearing: r.outcome.bearing,
        recordedAt: r.outcome.recordedAt,
      })),
    ),
  }
}

/* ── the rendering ─────────────────────────────────────────────────────── */

/**
 * An INSTANT, rendered as the UTC day it fell on. For `builtAt` and nothing else.
 *
 * When the pack was built is a moment in time, and a reader in another zone is
 * owed the same answer as the person who built it.
 */
// The READER's day, through `core/dates.ts`: a UTC slice cuts a day out of the
// instant for anyone west of Greenwich, which `dates.scan.test.ts` holds the
// whole tree to.
const isoDay = (t: number): string => calendarDay(t)

/**
 * A DAY somebody picked, rendered as the day they picked.
 *
 * `outcome.at` and `resolvesBy` are not instants. Both are written by a date
 * input as local midday on the chosen day — `new Date(y, m - 1, d, 12, 0, 0)` in
 * the outcome form and in the standing block — so the timestamp is a label for a
 * calendar day rather than a moment anything happened at. Passing one through
 * `toISOString` asks it the wrong question, and east of UTC+12 it gets the wrong
 * answer: local midday on 3 September is 2 September in UTC, so the pack printed
 * the day before the one the person selected, for both columns.
 *
 * This was decided the other way once, on the argument that a pack is read by
 * somebody in another zone and should therefore carry UTC days. That argument is
 * right about instants and wrong about these two: there is no instant here to
 * localise, only a day the person named, and the honest export of a named day is
 * that day.
 */
const pickedDay = (t: number): string => {
  const d = new Date(t)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** A cell's worth of free text: one line, and no pipe that would split it. */
const cell = (s: string): string => s.replace(/\s+/g, ' ').replace(/\|/g, '\\|').trim()

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`

/**
 * The pack as a document a person can read, paste into a message or diff.
 *
 * The summary first — one section per model, the counts, then the one-line
 * reading or the shortfall in its place, the same sentences the report prints
 * — and then one line per row, oldest observation first, with every date as an
 * ISO day. The selection-bias caveat travels with the numbers here as it does
 * on every other surface that prints them: these are the claims somebody chose
 * to test, and nothing about this rendering can correct for that.
 */
export function calibrationPackMarkdown(pack: CalibrationPack): string {
  const out: string[] = []
  const explorations = new Set(pack.rows.map((r) => r.sessionId)).size
  out.push('# Calibration pack')
  out.push('')
  out.push(
    `Built ${isoDay(pack.builtAt)} · ${plural(pack.rows.length, 'recorded result')} across ${plural(explorations, 'exploration')} · pack version ${pack.version}`,
  )
  out.push('')
  if (!pack.rows.length) {
    out.push('No recorded results yet. A claim gets one when somebody records what happened.')
    out.push('')
    return out.join('\n')
  }
  out.push(
    'These are the claims somebody chose to test, not a random sample of any graph: people test what they doubt, or what is cheap to test. Every number below carries that bias.',
  )
  out.push('')

  for (const c of pack.summary) {
    out.push(`## ${c.model.name} (${c.key})`)
    out.push('')
    const settled = c.inconclusive
      ? ` ${plural(c.inconclusive, 'further result')} settled nothing, counted and never scored.`
      : ''
    out.push(
      `- ${c.supported} of ${plural(c.scored, 'tested claim')} held up, across ${plural(c.explorations, 'exploration')}.${settled}`,
    )
    // One of the two, never both: the shortfall says what is missing until
    // there is a score, and the verdict is what the score says once there is.
    const line = verdictOf(c) ?? scoredShortfall(c)
    if (line) out.push(`- ${line}`)
    if (c.brier !== null && c.baseline !== null && c.drift !== null) {
      const dir = c.drift > 0 ? 'above' : c.drift < 0 ? 'below' : 'level with'
      out.push(
        `- Brier ${c.brier.toFixed(3)}, against ${c.baseline.toFixed(3)} for a rating that ignored the claim and always said the base rate; its mean rating sat ${Math.abs(c.drift).toFixed(2)} ${dir} what happened.`,
      )
    }
    out.push('')
  }

  out.push('## Rows')
  out.push('')
  out.push('| Observed | Bearing | Rated | Model | Claim | Exploration |')
  out.push('| --- | --- | --- | --- | --- | --- |')
  for (const r of pack.rows) {
    const due = r.resolvesBy !== undefined ? ` (due ${pickedDay(r.resolvesBy)})` : ''
    out.push(
      `| ${pickedDay(r.outcome.at)} | ${r.outcome.bearing} | ${r.confidence.toFixed(2)} | ${cell(r.model.name)} | ${cell(r.claim)}${due} | ${cell(r.seed)} · ${r.nodeId} |`,
    )
  }
  out.push('')
  return out.join('\n')
}

/* ── the library ───────────────────────────────────────────────────────── */

/**
 * Every exploration the library lists, parsed one at a time and let go.
 *
 * The INDEX, not the mirror's key list, for the reason every other library
 * reader walks it: the index is the library as the sidebar shows it, the two
 * are reconciled at boot, and the index row carries the demo flag and the
 * outcome count, so a pack skips the demo and every document whose row says
 * it holds no result without parsing either. A row written by an older build
 * has no count, and falls through to a read rather than trusting an absence.
 *
 * The queue does NOT skip the demo. Its results are authored, and the pack
 * refuses them by id as well as by row; but the demo carries no authored date
 * — a pre-written promise would be nonsense — so a date on one of its nodes is
 * one the person typed, and a promise the person made is theirs to be reminded
 * of, whichever document they made it in.
 *
 * The queue skips, by the same rule, every document whose row counts no node
 * carrying a date (`promises`): it can add no row to the list, and the list is
 * drawn in the sidebar on the first render of every page, where reading the whole
 * library used to stand between a reload and the first frame (D345). A row with
 * no count falls through to a read, as it does for results.
 *
 * The open exploration appears as its last SAVE; a caller who wants its live
 * state passes the session to the pure reader instead, and names it as `exceptId`
 * so its stored copy is never read at all.
 */
function* libraryDocs(need: 'promises' | 'results', exceptId?: string): Generator<Session> {
  for (const entry of listRecent()) {
    if (entry.id === exceptId) continue
    if (need === 'results' && (entry.demo || entry.outcomes === 0)) continue
    if (need === 'promises' && entry.promises === 0) continue
    const doc = loadSession(entry.id)
    if (doc) yield doc.session
  }
}

/**
 * The queue across every stored exploration. Parses each document once; call it
 * when the queue is opened, not per frame.
 *
 * `live` is the exploration OPEN right now, handed in rather than read off the
 * shelf, and it replaces its own stored copy. The autosave is debounced by more
 * than a second, so between typing a date and the write the shelf still holds
 * the document as it was: a promise just made would be missing from the list,
 * and a result just recorded would still be on it, until some unrelated later
 * revision happened to recompute. A queue that lags the thing it is about by a
 * second and a half is a queue nobody trusts.
 */
export function libraryResolutionQueue(now: number, live?: ResolutionSource): ResolutionRow[] {
  return resolutionQueue(withLive(libraryPromiseSources(), live), now)
}

/**
 * The stored half alone, PARSED — the expensive part, separated so a caller can
 * cache it on the shelf's identity.
 *
 * Every reader of the queue also depends on the open exploration, which changes
 * far more often than the shelf does and, through `setViewInset`, changes on a
 * pane toggle that touched no document at all. Folding the two together made a
 * window resize re-read and re-inflate every stored exploration. AGENTS.md
 * states the rule this restores: key on the identity of every field a write
 * would swap, and on NOTHING else.
 */
export function libraryPromiseSources(exceptId?: string): Session[] {
  // The open exploration is skipped BEFORE it is parsed, not discarded after.
  // `withLive` replaces its stored copy anyway, so reading it off the shelf was
  // work whose result was thrown away — and it is the one document that changes
  // most often, so it was also what kept invalidating the cache above it.
  return [...libraryDocs('promises', exceptId)]
}

/** The open exploration ahead of its own stored copy. It is handed in rather
 *  than read off the shelf for the reason above the queue. */
export function withLive<T extends ResolutionSource>(
  stored: readonly T[],
  live?: ResolutionSource,
): ResolutionSource[] {
  if (!live) return [...stored]
  return [live, ...stored.filter((s) => s.id !== live.id)]
}

/**
 * The pack across every stored exploration — the artefact a person carries.
 *
 * `live` on the same rule as the queue above, and for the same reason: the
 * shelf lags the open exploration by the autosave's second and a half, and the
 * one press that produces this pack is routinely the press right after
 * recording a result. Read off the shelf alone, the table could omit the
 * outcome just recorded, or carry one just undone — in a document whose entire
 * purpose is to say how often this person's expectations were met. Only the
 * queue took the live session when they were written, which is exactly the sort
 * of pair that drifts.
 */
export function libraryCalibrationPack(now: number, live?: ResolutionSource): CalibrationPack {
  const stored = [...libraryDocs('results')].filter((s) => !live || s.id !== live.id)
  return calibrationPack(live ? [live, ...stored] : stored, now)
}

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)
